import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography, IconButton, Fab } from '@mui/material';
import { CameraAlt, FlipCameraAndroid, PhotoCamera, ArrowBack } from '@mui/icons-material';
import Webcam from 'react-webcam';

interface CameraComponentProps {
    onCapture: (file: File) => void;
    onClose: () => void;
    isOpen: boolean;
}

export const CameraComponent: React.FC<CameraComponentProps> = ({ onCapture, onClose, isOpen }) => {
    const webcamRef = useRef(null) as any;

    const [cameraError, setCameraError] = useState(false);
    const [cameraPermission, setCameraPermission] = useState<boolean | null>(null);
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
    const [cameraSettings, setCameraSettings] = useState<{width: number, height: number} | null>(null);
    const [deviceOrientation, setDeviceOrientation] = useState<'portrait' | 'landscape'>('portrait');
    const [isMobile, setIsMobile] = useState(false);
    const [cameraKey, setCameraKey] = useState(0);
    const [currentStream, setCurrentStream] = useState<MediaStream | null>(null);
    const [isReconfiguring, setIsReconfiguring] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        if (isOpen) {
            detectDeviceType();
            setupOrientationListener();
            checkCameraPermission();
        }
        return () => {
            if (currentStream) {
                currentStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [isOpen]);

    // Solo reconfigurar en móviles si hay un cambio significativo de orientación
    useEffect(() => {
        if (isMobile && isOpen && !isReconfiguring && retryCount < 3) {
            const shouldReconfigure = deviceOrientation === 'landscape' && 
                cameraSettings && 
                cameraSettings.width < cameraSettings.height;
            
            if (shouldReconfigure) {
                console.log('🔄 Reconfigurando cámara por cambio de orientación...');
                setIsReconfiguring(true);
                
                if (currentStream) {
                    currentStream.getTracks().forEach(track => track.stop());
                    setCurrentStream(null);
                }
                
                const timer = setTimeout(async () => {
                    try {
                        const newConstraints = getOptimizedVideoConstraints();
                        const newStream = await navigator.mediaDevices.getUserMedia({
                            video: newConstraints
                        });
                        
                        setCurrentStream(newStream);
                        setCameraKey(prev => prev + 1);
                        setRetryCount(0); // Reset retry count on success
                    } catch (error) {
                        console.error('Error reconfigurando cámara:', error);
                        setRetryCount(prev => prev + 1);
                    } finally {
                        setIsReconfiguring(false);
                    }
                }, 300);
                
                return () => clearTimeout(timer);
            }
        }
    }, [deviceOrientation, isMobile, isOpen, currentStream, isReconfiguring, retryCount, cameraSettings]);

    const detectDeviceType = () => {
        const userAgent = navigator.userAgent.toLowerCase();
        const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
        setIsMobile(isMobileDevice);
        
        detectOrientation();
    };

    const detectOrientation = () => {
        if (window.screen && window.screen.orientation) {
            const orientation = window.screen.orientation.type;
            setDeviceOrientation(orientation.includes('landscape') ? 'landscape' : 'portrait');
        } else {
            setDeviceOrientation(window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');
        }
    };

    const setupOrientationListener = () => {
        const handleOrientationChange = () => {
            detectOrientation();
        };

        window.addEventListener('orientationchange', handleOrientationChange);
        window.addEventListener('resize', handleOrientationChange);

        return () => {
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.removeEventListener('resize', handleOrientationChange);
        };
    };

    const getOptimizedVideoConstraints = () => {
        // Constraints más conservadores para mejor compatibilidad
        const baseConstraints = {
            facingMode: facingMode,
        };

        // Para móviles, usar resoluciones más bajas y compatibles
        if (isMobile) {
            return {
                ...baseConstraints,
                width: { ideal: 1280, min: 640, max: 1920 },
                height: { ideal: 720, min: 480, max: 1080 },
                aspectRatio: { ideal: 16/9 }
            };
        }

        // Para desktop
        return {
            ...baseConstraints,
            width: { ideal: 1280, min: 640, max: 1920 },
            height: { ideal: 720, min: 480, max: 1080 },
            aspectRatio: { ideal: 16/9 }
        };
    };

    const getFallbackConstraints = () => {
        // Constraints mínimos como fallback
        return {
            facingMode: facingMode,
            width: { min: 320, ideal: 640, max: 1280 },
            height: { min: 240, ideal: 480, max: 720 }
        };
    };

    const checkCameraPermission = async () => {
        try {
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                stream.getTracks().forEach(track => track.stop());
                setCameraPermission(true);
            } else {
                setCameraPermission(false);
            }
        } catch (err) {
            console.error('Error checking camera permission:', err);
            setCameraPermission(false);
        }
    };

    const switchCamera = () => {
        setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    };

    const handleCapture = useCallback(() => {
        if (webcamRef.current) {
            const videoElement = webcamRef.current.video;
            if (!videoElement) {
                return;
            }

            const videoWidth = videoElement.videoWidth;
            const videoHeight = videoElement.videoHeight;

            const imageSrc = webcamRef.current.getScreenshot({
                width: videoWidth,
                height: videoHeight
            });
            
            if (imageSrc) {
                const byteString = atob(imageSrc.split(',')[1]);
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }
                
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                const filename = `camera_${videoWidth}x${videoHeight}_${timestamp}.png`;
                
                const file = new File([ab], filename, {type: 'image/png'});
                
                // Detener el stream inmediatamente antes de llamar onCapture
                if (currentStream) {
                    currentStream.getTracks().forEach(track => track.stop());
                    setCurrentStream(null);
                }
                
                onCapture(file);
            }
        }
    }, [webcamRef, onCapture, currentStream]);

    const handleCameraError = (error: any) => {
        console.error('Camera error:', error);
        setCameraError(true);
        setCameraPermission(false);
    };

    const handleUserMedia = (stream: MediaStream) => {
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
            const settings = videoTrack.getSettings();
            
            if (settings.width && settings.height) {
                setCameraSettings({
                    width: settings.width,
                    height: settings.height
                });
                
                console.log('Camera settings:', settings);
            }
        }
    };

    const initializeCamera = async () => {
        setCameraError(false);
        setCameraKey(0);
        setIsReconfiguring(true);
        setRetryCount(0);
        
        try {
            // Intentar con constraints optimizados primero
            const initialConstraints = getOptimizedVideoConstraints();
            console.log('Trying optimized constraints:', initialConstraints);
            
            const stream = await navigator.mediaDevices.getUserMedia({
                video: initialConstraints
            });
            
            setCurrentStream(stream);
            console.log('Camera initialized successfully');
        } catch (error) {
            console.error('Failed with optimized constraints, trying fallback:', error);
            
            try {
                // Intentar con constraints de fallback
                const fallbackConstraints = getFallbackConstraints();
                console.log('Trying fallback constraints:', fallbackConstraints);
                
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: fallbackConstraints
                });
                
                setCurrentStream(stream);
                console.log('Camera initialized with fallback constraints');
            } catch (fallbackError) {
                console.error('Failed with fallback constraints:', fallbackError);
                setCameraError(true);
            }
        } finally {
            setIsReconfiguring(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            initializeCamera();
        } else {
            if (currentStream) {
                currentStream.getTracks().forEach(track => track.stop());
                setCurrentStream(null);
            }
            setCameraSettings(null);
            setIsReconfiguring(false);
            setRetryCount(0);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: '#000',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 }
            }
        }}>
            <Box sx={{
                position: 'absolute',
                top: 20,
                left: 20,
                zIndex: 10
            }}>
                <IconButton 
                    onClick={() => {
                        if (currentStream) {
                            currentStream.getTracks().forEach(track => track.stop());
                            setCurrentStream(null);
                        }
                        onClose();
                    }} 
                    sx={{ 
                        color: 'white',
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.5)'
                        }
                    }}
                >
                    <ArrowBack />
                </IconButton>
            </Box>

            <Box sx={{
                position: 'absolute',
                top: 20,
                right: 20,
                zIndex: 10
            }}>
                <IconButton 
                    onClick={switchCamera} 
                    sx={{ 
                        color: 'white',
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.5)'
                        }
                    }}
                >
                    <FlipCameraAndroid />
                </IconButton>
            </Box>

            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
            }}>
                {!cameraError ? (
                    <>
                        {isReconfiguring ? (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                textAlign: 'center',
                                padding: 3
                            }}>
                                <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Inicializando cámara...
                                </Typography>
                                <Typography variant="body2" color="rgba(255,255,255,0.8)">
                                    {retryCount > 0 ? `Reintento ${retryCount}/3` : 'Configurando...'}
                                </Typography>
                            </Box>
                        ) : (
                            <Webcam
                                key={cameraKey}
                                audio={false}
                                ref={webcamRef}
                                screenshotFormat="image/png"
                                videoConstraints={getOptimizedVideoConstraints()}
                                onUserMediaError={handleCameraError}
                                onUserMedia={handleUserMedia}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                        )}
                    </>
                ) : (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        textAlign: 'center',
                        padding: 3
                    }}>
                        <Typography variant="h6" gutterBottom>
                            Error de Cámara
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                            No se pudo acceder a la cámara. Verifique los permisos.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={() => {
                                if (currentStream) {
                                    currentStream.getTracks().forEach(track => track.stop());
                                    setCurrentStream(null);
                                }
                                onClose();
                            }}
                            sx={{ mt: 2 }}
                        >
                            Volver
                        </Button>
                    </Box>
                )}
            </Box>

            {!cameraError && !isReconfiguring && (
                <Box sx={{
                    position: 'absolute',
                    bottom: 40,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <Fab
                        color="primary"
                        size="large"
                        onClick={handleCapture}
                        sx={{
                            width: 80,
                            height: 80,
                            backgroundColor: 'white',
                            color: 'black',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                            '&:hover': {
                                backgroundColor: '#f0f0f0',
                                boxShadow: '0 6px 25px rgba(0,0,0,0.4)'
                            }
                        }}
                    >
                        <PhotoCamera sx={{ fontSize: 40 }} />
                    </Fab>
                </Box>
            )}
        </Box>
    );
}; 